import { useCallback, useState } from "react"
import { from, firstValueFrom } from "rxjs"
import { catchError, finalize } from "rxjs/operators"

export interface MutationState<TData, TError> {
    isLoading: boolean
    isError: boolean
    error: TError | null
    data: TData | null
}

export interface MutationResult<TData, TError>
    extends MutationState<TData, TError> {
    mutate: (variables: any) => void
    mutateAsync: (variables: any) => Promise<TData>
}

export function useHttpMutation<TData = unknown, TError = unknown>(
    mutationFn: (variables: any) => Promise<TData>,
): MutationResult<TData, TError> {
    const [state, setState] = useState<MutationState<TData, TError>>({
        isLoading: false,
        isError: false,
        error: null,
        data: null,
    })

    const mutate = useCallback(
        (variables: any) => {
            setState((prev) => ({ ...prev, isLoading: true }))

            from(mutationFn(variables))
                .pipe(
                    catchError((error) => {
                        setState((prev) => ({
                            ...prev,
                            isError: true,
                            error: error as TError,
                        }))
                        throw error
                    }),
                    finalize(() => {
                        setState((prev) => ({ ...prev, isLoading: false }))
                    }),
                )
                .subscribe({
                    next: (data) => {
                        setState((prev) => ({
                            ...prev,
                            data,
                            isError: false,
                            error: null,
                        }))
                    },
                })
        },
        [mutationFn],
    )

    const mutateAsync = useCallback(
        async (variables: any): Promise<TData> => {
            setState((prev) => ({ ...prev, isLoading: true }))

            try {
                const data = await firstValueFrom(
                    from(mutationFn(variables)).pipe(
                        catchError((error) => {
                            setState((prev) => ({
                                ...prev,
                                isError: true,
                                error: error as TError,
                            }))
                            throw error
                        }),
                    ),
                )

                setState((prev) => ({
                    ...prev,
                    data,
                    isError: false,
                    error: null,
                }))

                return data
            } finally {
                setState((prev) => ({ ...prev, isLoading: false }))
            }
        },
        [mutationFn],
    )

    return {
        ...state,
        mutate,
        mutateAsync,
    }
}
